import { use, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { RegistryContext, useAtom, useAtomValue } from '@effect/atom-react';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import { apiMutation, apiQuery } from '@listening/frontend/query/api-runtime';
import { AsyncResult, Atom, type AtomRegistry } from 'effect/reactivity';
import { adminKeys } from '@/components/web-fe/admin/query-keys';
import { authClient } from '@/lib/auth-client';
import { useImpersonate } from '@/components/web-fe/admin/use-impersonate';
import {
  baFailureMessage,
  baRequest,
} from '@/components/web-fe/admin/unwrap-ba-response';
import {
  ForbiddenError,
  type SubscriptionHistoryRow,
} from '@listening/api-contract';
import { Button } from '@listening/frontend/components/radix/button';
import { Input } from '@listening/frontend/components/radix/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@listening/frontend/components/radix/dialog';
import { rootLogger } from '@/lib/root-logger';
import { Cause, Effect, Exit, Option, Schema } from 'effect';

const moduleLogger = rootLogger.child({ module: 'users.$userId' });
const isForbidden = Schema.is(ForbiddenError);

const userDetailAtom = Atom.family((userId: string) =>
  apiQuery(
    Effect.flatMap(ApiClient, (api) =>
      api.Admin.GetUserDetail({ params: { userId } }),
    ),
  ),
);

const subscriptionHistoryAtom = Atom.family((userId: string) =>
  apiQuery(
    Effect.flatMap(ApiClient, (api) =>
      api.Admin.GetUserSubscriptionHistory({
        params: { userId },
        query: { pageIndex: 0, pageSize: 50 },
      }),
    ),
  ),
);

/** Refetches one user's pages. */
const refreshUserPages = (
  registry: AtomRegistry.AtomRegistry,
  userId: string,
) => {
  registry.refresh(userDetailAtom(userId));
  registry.refresh(subscriptionHistoryAtom(userId));
};

/** Re-syncs a user's billing from Stripe, then refetches their pages. */
const syncBillingAtom = Atom.family((userId: string) =>
  apiMutation((_: void, get) =>
    Effect.flatMap(ApiClient, (api) =>
      api.Admin.SyncUserBilling({ params: { userId } }),
    ).pipe(
      Effect.tap(() =>
        Effect.sync(() => refreshUserPages(get.registry, userId)),
      ),
    ),
  ),
);

const setUserPasswordAtom = Atom.fn(
  (request: { readonly userId: string; readonly newPassword: string }) =>
    baRequest(() => authClient.admin.setUserPassword(request)),
);

const updateUserEmailAtom = Atom.fn(
  (request: { readonly userId: string; readonly email: string }) =>
    baRequest(() =>
      authClient.admin.updateUser({
        userId: request.userId,
        data: { email: request.email },
      }),
    ),
);

const deleteUserAtom = apiMutation(
  (userId: string) =>
    Effect.flatMap(ApiClient, (api) =>
      api.Admin.DeleteUserAccount({ params: { userId } }),
    ),
  { reactivityKeys: [adminKeys.users] },
);

export const Route = createFileRoute('/app/_authenticated/admin/users/$userId')(
  {
    validateSearch: Schema.toStandardSchemaV1(
      Schema.Struct({ impersonate: Schema.optional(Schema.Boolean) }),
    ),
    component: UserDetailPage,
  },
);

function UserDetailPage() {
  const { userId } = Route.useParams();
  const { impersonate } = Route.useSearch();
  const registry = use(RegistryContext);

  const detailResult = useAtomValue(userDetailAtom(userId));
  const subscriptionHistoryResult = useAtomValue(
    subscriptionHistoryAtom(userId),
  );
  const subscriptionHistory = AsyncResult.getOrElse(
    subscriptionHistoryResult,
    () => undefined,
  );

  const [syncBillingResult, syncBilling] = useAtom(syncBillingAtom(userId), {
    mode: 'promiseExit',
  });
  const syncBillingAndReport = async () => {
    const exit = await syncBilling();
    if (Exit.isFailure(exit)) {
      moduleLogger.error(
        { err: Cause.squash(exit.cause) },
        'Failed to sync billing',
      );
      toast.error('Failed to sync billing');
    } else if (exit.value.errors.length > 0) {
      toast.warning(
        `Sync completed with errors: ${exit.value.errors.join(', ')}`,
      );
    } else {
      toast.success('Billing data refreshed');
    }
  };

  const [passwordDialog, setPasswordDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [impersonateDialog, setImpersonateDialog] = useState(
    impersonate === true,
  );
  const [deleteDialog, setDeleteDialog] = useState(false);
  const impersonateMutation = useImpersonate();

  if (AsyncResult.isInitial(detailResult)) {
    return (
      <div className='py-12 text-center text-gray-500 dark:text-steel-180'>
        Loading user details...
      </div>
    );
  }

  if (AsyncResult.isFailure(detailResult)) {
    return (
      <div className='py-12 text-center text-red-500'>
        Failed to load user details.
      </div>
    );
  }

  const user = detailResult.value;

  return (
    <div>
      <Link
        to='/app/admin'
        className='mb-4 inline-flex text-sm text-gray-500 hover:text-gray-700 dark:text-steel-180 dark:hover:text-white'
      >
        &larr; Back to users
      </Link>

      <div className='mb-6 flex items-start justify-between'>
        <div>
          <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
            {user.name || 'No name'}
          </h2>
          <p className='text-gray-500 dark:text-steel-180'>{user.email}</p>
        </div>
        <div className='flex gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setPasswordDialog(true)}
          >
            Set Password
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setEmailDialog(true)}
          >
            Set Email
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setImpersonateDialog(true)}
          >
            Impersonate
          </Button>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => setDeleteDialog(true)}
          >
            Delete User
          </Button>
        </div>
      </div>

      {/* Account Info */}
      <Section title='Account Info'>
        <Field label='ID' value={user.id} />
        <Field label='Email' value={user.email} />
        <Field
          label='Email Verified'
          value={user.emailVerified ? 'Yes' : 'No'}
        />
        <Field label='Role' value={user.role ?? 'user'} />
        <Field
          label='Status'
          value={
            user.banned
              ? `Banned${user.banReason ? ` (${user.banReason})` : ''}`
              : 'Active'
          }
        />
        {user.banExpires && (
          <Field label='Ban Expires' value={formatDate(user.banExpires)} />
        )}
        <Field label='Created' value={formatDate(user.createdAt)} />
        <Field label='Updated' value={formatDate(user.updatedAt)} />
        <Field label='Referral Code' value={user.referralCode ?? 'None'} />
        {user.deletedAt && (
          <Field label='Deleted At' value={formatDate(user.deletedAt)} />
        )}
      </Section>

      {/* Subscription / Billing */}
      <Section
        title='Billing'
        action={
          <Button
            variant='outline'
            size='sm'
            onClick={() => void syncBillingAndReport()}
            disabled={syncBillingResult.waiting}
          >
            {syncBillingResult.waiting ? 'Syncing...' : 'Sync Billing'}
          </Button>
        }
      >
        {user.billing ? (
          <>
            <h4 className='mt-3 mb-2 text-sm font-semibold text-gray-700 dark:text-steel-180'>
              Stripe
            </h4>
            <Field
              label='Customer ID'
              value={user.billing.stripeCustomerId ?? 'None'}
            />
            <Field
              label='Subscription ID'
              value={user.billing.stripeSubscriptionId ?? 'None'}
            />
            <Field
              label='Status'
              value={user.billing.stripeSubscriptionStatus ?? 'None'}
            />
            <Field
              label='Current Period End'
              value={formatDate(user.billing.stripeCurrentPeriodEnd)}
            />
            <Field
              label='Cancel at Period End'
              value={user.billing.stripeCancelAtPeriodEnd ? 'Yes' : 'No'}
            />
            <Field
              label='Trial End'
              value={formatDate(user.billing.stripeTrialEnd)}
            />
            <Field
              label='Last Synced'
              value={formatDate(user.billing.lastSyncedStripeAt)}
            />

            <h4 className='mt-3 mb-2 text-sm font-semibold text-gray-700 dark:text-steel-180'>
              RevenueCat
            </h4>
            <Field
              label='Status'
              value={user.billing.rcSubscriptionStatus ?? 'None'}
            />
            <Field
              label='Current Period End'
              value={formatDate(user.billing.rcCurrentPeriodEnd)}
            />
            <Field
              label='Trial End'
              value={formatDate(user.billing.rcTrialEnd)}
            />
            <Field
              label='Last Synced'
              value={formatDate(user.billing.lastSyncedRevenueCatAt)}
            />

            <h4 className='mt-3 mb-2 text-sm font-semibold text-gray-700 dark:text-steel-180'>
              Referral
            </h4>
            <Field
              label='Start'
              value={formatDate(user.billing.referralStartAt)}
            />
            <Field label='End' value={formatDate(user.billing.referralEndAt)} />
            <Field
              label='Referred By'
              value={user.billing.referredByUserId ?? 'None'}
            />

            <h4 className='mt-3 mb-2 text-sm font-semibold text-gray-700 dark:text-steel-180'>
              General
            </h4>
            <Field
              label='Has Ever Paid'
              value={user.billing.hasEverPaid ? 'Yes' : 'No'}
            />
          </>
        ) : (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            No billing record
          </p>
        )}
      </Section>

      {/* Subscription History */}
      <Section
        title='Subscription History'
        action={
          subscriptionHistory ? (
            <span className='text-xs text-gray-500 dark:text-steel-180'>
              {subscriptionHistory.total} event
              {subscriptionHistory.total === 1 ? '' : 's'}
            </span>
          ) : null
        }
      >
        {AsyncResult.isInitial(subscriptionHistoryResult) ? (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            Loading...
          </p>
        ) : AsyncResult.isFailure(subscriptionHistoryResult) ? (
          <p className='text-sm text-red-500'>Failed to load</p>
        ) : subscriptionHistory?.events.length === 0 ? (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            No events yet
          </p>
        ) : (
          <SubscriptionHistoryTable
            events={subscriptionHistory?.events ?? []}
          />
        )}
      </Section>

      {/* Preferences */}
      <Section title='Preferences'>
        {user.preferences ? (
          <Field label='TTS Voice' value={user.preferences.voice} />
        ) : (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            No preferences set
          </p>
        )}
      </Section>

      {/* Marketing Survey */}
      <Section title='Marketing Survey'>
        {user.marketingSurvey ? (
          <>
            <Field
              label='Acquisition Source'
              value={user.marketingSurvey.acquisitionSource}
            />
            <Field
              label='Details'
              value={user.marketingSurvey.details ?? 'None'}
            />
          </>
        ) : (
          <p className='text-sm text-gray-500 dark:text-steel-180'>
            No survey submitted
          </p>
        )}
      </Section>

      {/* Impersonate Dialog */}
      <Dialog open={impersonateDialog} onOpenChange={setImpersonateDialog}>
        <DialogContent includeCloseButton>
          <DialogHeader>
            <DialogTitle>Confirm Impersonation</DialogTitle>
            <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
              You are about to impersonate <strong>{user.email}</strong>.
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setImpersonateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void impersonateMutation.impersonate(user.id)}
              disabled={impersonateMutation.isPending}
            >
              {impersonateMutation.isPending
                ? 'Impersonating...'
                : 'Impersonate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        {passwordDialog && (
          <ChangePasswordDialog
            userId={user.id}
            email={user.email}
            onClose={() => setPasswordDialog(false)}
          />
        )}
      </Dialog>

      {/* Set Email Dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        {emailDialog && (
          <ChangeEmailDialog
            userId={user.id}
            currentEmail={user.email}
            onClose={() => {
              setEmailDialog(false);
              refreshUserPages(registry, userId);
            }}
          />
        )}
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        {deleteDialog && (
          <DeleteUserDialog
            userId={user.id}
            email={user.email}
            role={user.role}
            onClose={() => setDeleteDialog(false)}
          />
        )}
      </Dialog>
    </div>
  );
}

// =============================================================================
// Helper Components
// =============================================================================

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className='mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-260 dark:bg-navy-760'>
      <div className='mb-3 flex items-center justify-between'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex border-b border-gray-100 py-1.5 last:border-0 dark:border-navy-260'>
      <span className='w-48 shrink-0 text-sm font-medium text-gray-500 dark:text-steel-180'>
        {label}
      </span>
      <span className='text-sm text-gray-900 dark:text-white'>{value}</span>
    </div>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleString();
}

function SubscriptionHistoryTable({
  events,
}: {
  events: readonly SubscriptionHistoryRow[];
}) {
  return (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='border-b border-gray-200 text-left text-xs text-gray-500 uppercase dark:border-navy-260 dark:text-steel-180'>
            <th className='py-2 pr-4'>When</th>
            <th className='py-2 pr-4'>Provider</th>
            <th className='py-2 pr-4'>Event</th>
            <th className='py-2 pr-4'>Source</th>
            <th className='py-2 pr-4'>Period End</th>
            <th className='py-2 pr-4'>Provider Event ID</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr
              key={e.id}
              className='border-b border-gray-100 align-top dark:border-navy-260'
            >
              <td className='py-2 pr-4 whitespace-nowrap text-gray-900 dark:text-white'>
                {formatDate(e.createdAt)}
              </td>
              <td className='py-2 pr-4 text-gray-700 dark:text-steel-180'>
                {e.provider}
              </td>
              <td className='py-2 pr-4 font-medium text-gray-900 dark:text-white'>
                {e.eventType}
              </td>
              <td className='py-2 pr-4 text-gray-700 dark:text-steel-180'>
                {e.eventSource}
              </td>
              <td className='py-2 pr-4 text-gray-700 dark:text-steel-180'>
                {formatDate(e.periodEndAt)}
              </td>
              <td className='py-2 pr-4 font-mono text-xs text-gray-500 dark:text-steel-180'>
                {e.providerEventId ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Dialogs
// =============================================================================

function ChangePasswordDialog({
  userId,
  email,
  onClose,
}: {
  userId: string;
  email: string;
  onClose: () => void;
}) {
  const [newPassword, setNewPassword] = useState('');

  const [result, setUserPassword] = useAtom(setUserPasswordAtom, {
    mode: 'promiseExit',
  });
  const isPending = result.waiting;

  return (
    <DialogContent includeCloseButton>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!newPassword.trim()) return;
          void setUserPassword({ userId, newPassword }).then((exit) => {
            if (Exit.isSuccess(exit)) {
              toast.success('Password updated');
              onClose();
              return;
            }
            moduleLogger.error(
              { err: Cause.squash(exit.cause) },
              'Failed to update password',
            );
            toast.error(
              baFailureMessage(exit.cause, 'Failed to update password'),
            );
          });
        }}
      >
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
            Set a new password for {email}
          </p>
        </DialogHeader>
        <div className='py-4'>
          <Input
            type='password'
            placeholder='New password'
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant='outline' type='button' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' disabled={isPending || !newPassword.trim()}>
            {isPending ? 'Saving...' : 'Set Password'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function ChangeEmailDialog({
  userId,
  currentEmail,
  onClose,
}: {
  userId: string;
  currentEmail: string;
  onClose: () => void;
}) {
  const [newEmail, setNewEmail] = useState(currentEmail);

  const [result, updateUserEmail] = useAtom(updateUserEmailAtom, {
    mode: 'promiseExit',
  });
  const isPending = result.waiting;

  return (
    <DialogContent includeCloseButton>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!newEmail.trim() || newEmail === currentEmail) return;
          void updateUserEmail({ userId, email: newEmail.trim() }).then(
            (exit) => {
              if (Exit.isSuccess(exit)) {
                toast.success('Email updated');
                onClose();
                return;
              }
              moduleLogger.error(
                { err: Cause.squash(exit.cause) },
                'Failed to update email',
              );
              toast.error(
                baFailureMessage(exit.cause, 'Failed to update email'),
              );
            },
          );
        }}
      >
        <DialogHeader>
          <DialogTitle>Change Email</DialogTitle>
          <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
            Update the email address for this user
          </p>
        </DialogHeader>
        <div className='py-4'>
          <Input
            type='email'
            placeholder='New email'
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant='outline' type='button' onClick={onClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            disabled={
              isPending || !newEmail.trim() || newEmail === currentEmail
            }
          >
            {isPending ? 'Saving...' : 'Update Email'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function DeleteUserDialog({
  userId,
  email,
  role,
  onClose,
}: {
  userId: string;
  email: string;
  role: string | null | undefined;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [confirmEmail, setConfirmEmail] = useState('');
  const confirmed = confirmEmail.trim() === email;
  const isSelf = authClient.useSession().data?.user.id === userId;
  const warning = isSelf
    ? 'This is your own account. Deleting it signs you out and removes your admin access.'
    : role === 'admin' || role === 'staff'
      ? `This is a ${role} account. Deleting it also removes their admin access.`
      : null;

  const [result, deleteUser] = useAtom(deleteUserAtom, {
    mode: 'promiseExit',
  });
  const isPending = result.waiting;

  return (
    <DialogContent includeCloseButton>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!confirmed) return;
          void deleteUser(userId).then((exit) => {
            if (Exit.isSuccess(exit)) {
              toast.success('User deleted');
              onClose();
              void navigate({ to: '/app/admin' });
              return;
            }
            moduleLogger.error(
              { err: Cause.squash(exit.cause) },
              'Failed to delete user',
            );
            toast.error(
              Cause.findErrorOption(exit.cause).pipe(
                Option.filter(isForbidden),
                Option.match({
                  onNone: () => 'Failed to delete user',
                  onSome: (error) => error.message,
                }),
              ),
            );
          });
        }}
      >
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <p className='mt-1 text-sm text-gray-500 dark:text-steel-180'>
            Deletes <strong>{email}</strong> exactly as if they deleted their
            own account: their Stripe subscription is cancelled immediately
            without a refund, they are signed out everywhere, their library is
            removed, and they get a confirmation email. Type the email to
            confirm.
          </p>
        </DialogHeader>
        {warning === null ? null : (
          <p
            role='alert'
            className='mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300'
          >
            {warning}
          </p>
        )}
        <div className='py-4'>
          <Input
            type='email'
            placeholder={email}
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant='outline' type='button' onClick={onClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='destructive'
            disabled={isPending || !confirmed}
          >
            {isPending ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
